import { Box, Checkbox, Typography, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { PointOfInterestChecked } from '@/types';
import { generatePoiDisplayName } from '../SelectedPoiContent/utils';
import { formatDate, convertTimeStampToDate, isTimeStamp } from '@/utils';
import { Assembly, PaintRegion } from '@/__generated__/graphql';

type Props = {
  poiId: string;
  clickHandler?: () => undefined;
  handleCheckedPOI: Function;
  poi: PointOfInterestChecked;
  templateId: string;
  selectedPoiField: keyof PointOfInterestChecked | 'component';
};

const boxStyles = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  px: 2,
  py: 0,
  backgroundColor: '#f8f9fa',
  my: 0.5,
  cursor: 'pointer',
};

const ellipsisTypographySx = {
  fontSize: '12px',
  maxWidth: '20ch',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
};

export const PoiListItem = ({
  poiId,
  poi,
  handleCheckedPOI,
  templateId,
  clickHandler,
  selectedPoiField,
}: Props) => {
  const poiName = generatePoiDisplayName({
    name: poi.name,
    poiId: poiId,
    poiType: poi.type,
  });

  const selectedValueFormatted = useMemo(() => {
    if (!poi || !selectedPoiField || !(selectedPoiField in poi)) {
      return '';
    }

    const value = poi[selectedPoiField as keyof PointOfInterestChecked];

    if (typeof value === 'boolean' || selectedPoiField === 'isVerified') {
      return value ? 'Yes' : 'No';
    }

    if (!value) {
      return 'N/A';
    }

    if (selectedPoiField === 'assembly') return (value as Assembly).tagName;

    if (selectedPoiField === 'paintRegion') return (value as PaintRegion).name;

    if (typeof value === 'string' || typeof value === 'number') {
      if (isTimeStamp(value)) {
        const date = convertTimeStampToDate(String(value));
        if (!date || isNaN(date.getTime())) {
          return '';
        }
        return formatDate(date);
      }
    }

    return String(value);
  }, [poi, selectedPoiField]);

  return (
    <Box sx={boxStyles}>
      <Checkbox checked={poi.checked} onClick={handleCheckedPOI(poi, templateId)} />
      <Box
        sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
        onClick={clickHandler}
      >
        <Box sx={{ width: '50%' }}>
          <Tooltip title={poiName.length > 19 ? poiName : ''}>
            <Typography sx={ellipsisTypographySx}>{poiName}</Typography>
          </Tooltip>
        </Box>
        <Box sx={{ width: '50%' }}>
          <Tooltip title={selectedValueFormatted.length > 19 ? selectedValueFormatted : ''}>
            <Typography sx={{ ...ellipsisTypographySx, textAlign: 'right' }}>
              {selectedValueFormatted}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
